import { AxiosResponse } from 'axios';
import { useAxios } from '@/features/common/helpers/axios';
import { parseError } from '@/features/common/helpers/errorParser';
import { GlobalResponseType } from '@/features/common/types';

const { axiosService, baseURL } = useAxios();

export interface DocumentBlobResponse extends AxiosResponse<Blob> {
  data: Blob;
}

export interface UploadDocumentResponse {
  documentId: string;
}

export interface PollStatusResponse {
  documentId: string;
  status: 'Processing' | 'Clean' | 'Failed';
  failedReason?: string;
}

export const uploadDocument = async (
  file: File,
): Promise<{ documentId?: string } & GlobalResponseType> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalFileName', file.name);
    formData.append('mimeType', file.type);

    const response: AxiosResponse<UploadDocumentResponse> = await axiosService.post(
      '/documents/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    return {
      successful: true,
      documentId: response.data.documentId,
      redirectUrl: '',
      errorMessage: '',
    };
  } catch (err) {
    return parseError(err);
  }
};

export interface UpdateDocumentTypeResponse {
  success: boolean;
  documentId: string | null;
  documentType: string | null;
}

export const patchDocumentType = async (
  documentId: string,
  documentType: string,
): Promise<{ data?: UpdateDocumentTypeResponse } & GlobalResponseType> => {
  try {
    const response: AxiosResponse<UpdateDocumentTypeResponse> = await axiosService.patch(
      `/documents/${documentId}`,
      { documentType },
    );
    return {
      successful: true,
      data: response.data,
      redirectUrl: '',
      errorMessage: '',
    };
  } catch (err) {
    return parseError(err);
  }
};

export const pollDocumentStatuses = async (
  documentIds: string[],
): Promise<PollStatusResponse[] | null> => {
  try {
    const response: AxiosResponse<PollStatusResponse[]> = await axiosService.post(
      '/documents/status',
      { documentIds },
    );
    return response.data;
  } catch (err) {
    return null;
  }
};

export const getViewUrl = (documentId: string): string => {
  return `${baseURL}/documents/${documentId}/view`;
};

export const deleteDocument = async (
  documentId: string,
): Promise<boolean> => {
  try {
    await axiosService.delete(`/documents/${documentId}`);
    return true;
  } catch (err) {
    return false;
  }
};

export const fetchDocument = async (
  documentId: string,
): Promise<DocumentBlobResponse> => {
  return axiosService.get(`/documents/${documentId}/view`, {
    responseType: 'blob',
  });
};

export const fetchHistoryDocument = async (
  trackingNumber: string,
  documentId: string,
): Promise<DocumentBlobResponse> => {
  return axiosService.get(`/documents/${trackingNumber}/${documentId}/view`, {
    responseType: 'blob',
  });
};

export const extractFileName = (headers: Record<string, string>): string => {
  const contentDisposition = headers['content-disposition'];
  if (!contentDisposition) {
    return 'document';
  }

  const match = contentDisposition.match(/filename="?([^"]+)"?/);
  return match?.[1] || 'document';
};
